// helpContent.js
// Bilingual help center content (English / French) for 360 Carexport Order Manager.
// Each section: intro + ordered steps + permissions note + screenshot reference.
// Callouts use coordinate hints in % so the same data is reusable for the PDF.

export const LANGS = { en: 'English', fr: 'Français' };

export const SECTIONS = [
  {
    id: 'dashboard',
    icon: 'Gauge',
    title: { en: 'Dashboard', fr: 'Tableau de bord' },
    intro: {
      en: 'Your daily landing page. KPIs, latest orders, and quick alerts.',
      fr: 'Page d\'accueil quotidienne. Indicateurs, commandes récentes et alertes rapides.',
    },
    screenshot: 'dashboard.png',
    callouts: [
      { x: 8, y: 16, label: { en: 'Side navigation', fr: 'Menu latéral' },
        text: { en: 'All app sections. The list adapts to your role.', fr: 'Toutes les sections. La liste s\'adapte à votre rôle.' } },
      { x: 38, y: 28, label: { en: 'KPI cards', fr: 'Indicateurs clés' },
        text: { en: 'Live totals — orders, cars, revenue, outstanding balances.', fr: 'Totaux en direct — commandes, voitures, chiffre d\'affaires, soldes dus.' } },
      { x: 38, y: 70, label: { en: 'Latest orders', fr: 'Dernières commandes' },
        text: { en: 'Click any row to open the order drawer.', fr: 'Cliquez sur une ligne pour ouvrir la commande.' } },
      { x: 90, y: 8, label: { en: 'Profile menu', fr: 'Menu profil' },
        text: { en: 'Language switch (EN/FR/AR), notifications, logout.', fr: 'Changer la langue (EN/FR/AR), notifications, déconnexion.' } },
    ],
    permissions: { en: 'Visible to all logged-in users.', fr: 'Visible par tous les utilisateurs connectés.' },
  },
  {
    id: 'orders',
    icon: 'Package',
    title: { en: 'Orders', fr: 'Commandes' },
    intro: {
      en: 'The core workflow: create an order, add cars, follow it from China to Algeria, collect payments, issue documents.',
      fr: 'Le cœur du flux : créer une commande, ajouter les voitures, suivre de la Chine vers l\'Algérie, encaisser les paiements, émettre les documents.',
    },
    screenshot: 'orders.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ New Order', fr: '+ Nouvelle commande' },
        text: { en: 'Opens the order dialog. Pick client, supplier, assigned agent, notes — then add cars below.', fr: 'Ouvre la fenêtre de commande. Choisissez le client, le fournisseur, l\'agent assigné, notes — puis ajoutez les voitures.' } },
      { x: 16, y: 38, label: { en: 'Search & filters', fr: 'Recherche et filtres' },
        text: { en: 'Search by order #, client name or VIN. Filter by status and date range.', fr: 'Recherche par n° commande, nom client ou VIN. Filtres par statut et période.' } },
      { x: 50, y: 60, label: { en: 'Order row', fr: 'Ligne de commande' },
        text: { en: 'Click to open the drawer with cars, payments, timeline and document buttons.', fr: 'Cliquez pour ouvrir le panneau avec les voitures, paiements, historique et boutons de documents.' } },
      { x: 70, y: 12, label: { en: 'Import Excel', fr: 'Import Excel' },
        text: { en: 'Bulk import historical orders from a spreadsheet. 2-step preview before commit.', fr: 'Import massif depuis un tableur. Aperçu en 2 étapes avant validation.' } },
    ],
    steps: [
      { en: 'Click "+ New Order", select the Client (Individual or Dealer) and Supplier.', fr: 'Cliquez « + Nouvelle commande », choisissez le Client (Particulier ou Concessionnaire) et le Fournisseur.' },
      { en: 'Add one or more Cars (brand, model, color, VIN if known, selling price, CIF, shipping). Each car can have spare wheel and custom fees.', fr: 'Ajoutez une ou plusieurs voitures (marque, modèle, couleur, VIN si connu, prix de vente, CIF, transport). Chaque voiture peut avoir roue de secours et frais personnalisés.' },
      { en: 'Save. The order shows up immediately. Payments are added later via the Payments tab.', fr: 'Sauvegarder. La commande apparaît immédiatement. Les paiements s\'ajoutent ensuite via l\'onglet Paiements.' },
      { en: 'Open the order to update status (New → Loaded → Shipped → Arrived → Cleared → Delivered).', fr: 'Ouvrez la commande pour mettre à jour le statut (Nouvelle → Chargée → Expédiée → Arrivée → Dédouanée → Livrée).' },
      { en: 'Generate documents from the action buttons: Avis de Versement (PO), Bon de Livraison.', fr: 'Générez les documents depuis les boutons d\'action : Avis de Versement (PO), Bon de Livraison.' },
    ],
    permissions: { en: 'All sales agents can create orders. Restricted agents (with "See Own Orders Only") only see their own.', fr: 'Tous les agents commerciaux peuvent créer des commandes. Les agents restreints (avec « Voir uniquement ses propres commandes ») ne voient que les leurs.' },
  },
  {
    id: 'clients',
    icon: 'Users',
    title: { en: 'Clients', fr: 'Clients' },
    intro: {
      en: 'Manage individuals and dealers. The system blocks duplicates by name, NIN, passport or ID card.',
      fr: 'Gérez particuliers et concessionnaires. Le système empêche les doublons par nom, NIN, passeport ou carte d\'identité.',
    },
    screenshot: 'clients.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ Add Client', fr: '+ Ajouter Client' },
        text: { en: 'Choose Type (Individual or Dealer), fill identity fields. If a duplicate exists, a clear warning toast appears.', fr: 'Choisissez le Type (Particulier ou Concessionnaire), remplissez l\'identité. En cas de doublon, une alerte claire s\'affiche.' } },
      { x: 65, y: 12, label: { en: 'Import Excel', fr: 'Import Excel' },
        text: { en: 'Bulk-import clients via the template (download from the dialog).', fr: 'Import massif via le modèle (téléchargeable depuis la fenêtre).' } },
      { x: 22, y: 48, label: { en: 'Client type badge', fr: 'Étiquette type client' },
        text: { en: 'DEALER (orange) or INDIVIDUAL (gray) — visible on the row and across the app.', fr: 'DEALER (orange) ou INDIVIDUAL (gris) — visible sur la ligne et dans toute l\'application.' } },
    ],
    steps: [
      { en: 'Click "+ Add Client". Choose Individual or Dealer.', fr: 'Cliquez « + Ajouter Client ». Choisissez Particulier ou Concessionnaire.' },
      { en: 'Fill in name, phone, identity (NIN / Passport / ID card). For dealers, add company name.', fr: 'Remplissez le nom, téléphone, identité (NIN / Passeport / Carte d\'identité). Pour les concessionnaires, ajoutez la raison sociale.' },
      { en: 'If a duplicate is detected, you\'ll see the existing client\'s details — close and use the existing one instead.', fr: 'En cas de doublon, vous verrez les informations du client existant — fermez et utilisez le client existant.' },
      { en: 'Click on any client to see all their orders and edit details.', fr: 'Cliquez sur un client pour voir toutes ses commandes et modifier les informations.' },
    ],
    permissions: { en: 'Admin and sales agents can create/edit clients. Restricted agents see only their own clients.', fr: 'Admin et agents commerciaux peuvent créer/modifier les clients. Les agents restreints ne voient que leurs propres clients.' },
  },
  {
    id: 'suppliers',
    icon: 'Factory',
    title: { en: 'Suppliers', fr: 'Fournisseurs' },
    intro: {
      en: 'Chinese factories and exporters. Used when creating orders.',
      fr: 'Usines et exportateurs chinois. Utilisés lors de la création de commandes.',
    },
    screenshot: 'suppliers.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ Add Supplier', fr: '+ Ajouter Fournisseur' },
        text: { en: 'Add name, country, contact person and email.', fr: 'Ajoutez nom, pays, contact et email.' } },
    ],
    steps: [
      { en: 'Click "+ Add Supplier" and fill in the form.', fr: 'Cliquez « + Ajouter Fournisseur » et remplissez le formulaire.' },
      { en: 'Suppliers appear in the dropdown when creating an order.', fr: 'Les fournisseurs apparaissent dans la liste déroulante lors de la création d\'une commande.' },
    ],
    permissions: { en: 'Admin and logistics manager only.', fr: 'Admin et responsable logistique uniquement.' },
  },
  {
    id: 'car-catalog',
    icon: 'Car',
    title: { en: 'Car Catalog (Brands & Models)', fr: 'Catalogue (Marques & Modèles)' },
    intro: {
      en: 'Master list of brands and models with photos used in orders and the client portal.',
      fr: 'Liste maître des marques et modèles avec photos utilisés dans les commandes et le portail client.',
    },
    screenshot: 'brands.png',
    callouts: [
      { x: 50, y: 50, label: { en: 'Brand card with photo', fr: 'Carte marque avec photo' },
        text: { en: 'Click to edit. Upload a brand logo or model photo.', fr: 'Cliquez pour modifier. Téléversez un logo de marque ou photo de modèle.' } },
    ],
    steps: [
      { en: 'Open Brands → click a brand card to edit its logo.', fr: 'Ouvrez Marques → cliquez sur une carte pour modifier le logo.' },
      { en: 'Open Models to manage every brand\'s model lineup with photos.', fr: 'Ouvrez Modèles pour gérer la gamme de chaque marque avec photos.' },
      { en: 'Images upload to Emergent Object Storage and appear instantly in the order form and client portal.', fr: 'Les images sont stockées sur Emergent Object Storage et apparaissent instantanément dans le formulaire de commande et le portail client.' },
    ],
    permissions: { en: 'Admin only for create/edit. All roles can view.', fr: 'Admin uniquement pour création/modification. Tous les rôles peuvent consulter.' },
  },
  {
    id: 'stock-360',
    icon: 'Warehouse',
    title: { en: '360 Stock', fr: 'Stock 360' },
    intro: {
      en: 'Inventory of vehicles physically held in stock. Quantity decreases when added to an order.',
      fr: 'Inventaire des véhicules physiquement en stock. La quantité diminue lors de l\'ajout à une commande.',
    },
    screenshot: 'stock.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ Add Vehicle', fr: '+ Ajouter Véhicule' },
        text: { en: 'Brand, model, color, quantity, purchase + selling price.', fr: 'Marque, modèle, couleur, quantité, prix d\'achat + de vente.' } },
      { x: 35, y: 55, label: { en: 'Quantity badge', fr: 'Étiquette quantité' },
        text: { en: 'Auto-decrements when assigned to an order from stock.', fr: 'Diminue automatiquement quand affecté à une commande depuis le stock.' } },
    ],
    steps: [
      { en: 'Add stock with quantity, purchase price (USD) and DZD selling price.', fr: 'Ajoutez du stock avec quantité, prix d\'achat (USD) et prix de vente DZD.' },
      { en: 'In the order car form, pick "From Stock" — it auto-fills the car details.', fr: 'Dans le formulaire voiture, choisissez « Depuis Stock » — les détails se remplissent automatiquement.' },
    ],
    permissions: { en: 'Admin and logistics manager.', fr: 'Admin et responsable logistique.' },
  },
  {
    id: 'shipping',
    icon: 'Truck',
    title: { en: 'Shipping Companies', fr: 'Compagnies maritimes' },
    intro: {
      en: 'Carriers and their SCAC codes (used by Terminal49 to track containers).',
      fr: 'Transporteurs maritimes et codes SCAC (utilisés par Terminal49 pour le suivi).',
    },
    screenshot: 'shipping.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ Add Company', fr: '+ Ajouter Compagnie' },
        text: { en: 'Name, SCAC code (e.g. MSCU, CMDU, MAEU). Price per unit if applicable.', fr: 'Nom, code SCAC (ex. MSCU, CMDU, MAEU). Prix par unité si applicable.' } },
    ],
    steps: [
      { en: 'Add each carrier you work with — MSC, CMA-CGM, Maersk, etc.', fr: 'Ajoutez chaque transporteur avec lequel vous travaillez — MSC, CMA-CGM, Maersk, etc.' },
      { en: 'When creating a container, pick the carrier and the Terminal49 tracking link auto-resolves the SCAC.', fr: 'À la création d\'un conteneur, choisissez le transporteur — le lien Terminal49 résout automatiquement le SCAC.' },
    ],
    permissions: { en: 'Admin and logistics manager.', fr: 'Admin et responsable logistique.' },
  },
  {
    id: 'containers',
    icon: 'Truck',
    title: { en: 'Containers', fr: 'Conteneurs' },
    intro: {
      en: 'Group cars into shipping containers, track them via Terminal49, edit metadata, rename, add/remove cars, delete.',
      fr: 'Regroupez les voitures dans des conteneurs maritimes, suivez via Terminal49, modifiez les métadonnées, renommez, ajoutez/retirez des voitures, supprimez.',
    },
    screenshot: 'containers.png',
    callouts: [
      { x: 92, y: 10, label: { en: 'Create Container', fr: 'Créer Conteneur' },
        text: { en: 'Opens a dialog with a car search box, "Show unassigned only" toggle, and conflict-resolution when a car is already in another container.', fr: 'Ouvre une fenêtre avec recherche de voitures, bascule « Non assignées uniquement », et résolution de conflit si la voiture est déjà ailleurs.' } },
      { x: 88, y: 40, label: { en: 'Edit (pencil)', fr: 'Modifier (crayon)' },
        text: { en: 'Rename, update vessel/BL/ETA, add more cars. Changes propagate to all cars + Terminal49 tracking.', fr: 'Renommer, mettre à jour navire/BL/ETA, ajouter d\'autres voitures. Les changements s\'appliquent à toutes les voitures + suivi Terminal49.' } },
      { x: 88, y: 47, label: { en: 'Delete (trash)', fr: 'Supprimer (corbeille)' },
        text: { en: 'Deletes the container — cars become unassigned (orders intact).', fr: 'Supprime le conteneur — les voitures sont désassignées (commandes intactes).' } },
      { x: 30, y: 70, label: { en: 'Expand to remove single cars', fr: 'Déplier pour retirer une voiture' },
        text: { en: 'Click the row to expand; the X button next to each car removes just that car.', fr: 'Cliquez pour déplier ; le bouton X retire une voiture seule.' } },
    ],
    steps: [
      { en: 'Click "Create Container", enter the container # (e.g. MSCU1234567).', fr: 'Cliquez « Créer Conteneur », saisissez le n° (ex. MSCU1234567).' },
      { en: 'Search and tick the cars to add. Toggle "Show unassigned only" off to grab cars currently in other containers.', fr: 'Recherchez et cochez les voitures. Désactivez « Non assignées uniquement » pour récupérer des voitures déjà ailleurs.' },
      { en: 'If you pick a car already assigned, a conflict dialog asks before moving it.', fr: 'Si vous cochez une voiture déjà assignée, une fenêtre de confirmation demande avant de la déplacer.' },
      { en: 'Use the pencil to edit/rename. Use the trash to delete (cars become unassigned).', fr: 'Crayon pour modifier/renommer. Corbeille pour supprimer (voitures désassignées).' },
    ],
    permissions: { en: 'Admin and logistics manager.', fr: 'Admin et responsable logistique.' },
  },
  {
    id: 'loading-groups',
    icon: 'Stack',
    title: { en: 'Loading Groups', fr: 'Groupes de chargement' },
    intro: {
      en: 'Auto-detects cars belonging to the same dealer or family surname so you can load them into one container at customs.',
      fr: 'Détecte automatiquement les voitures du même concessionnaire ou de la même famille pour les regrouper dans un seul conteneur au dédouanement.',
    },
    screenshot: 'loading-groups.png',
    callouts: [
      { x: 30, y: 30, label: { en: 'Dealers / Families tabs', fr: 'Onglets Concessionnaires / Familles' },
        text: { en: 'Switch between dealer-based and family-name-based groupings.', fr: 'Basculer entre regroupements par concessionnaire et par nom de famille.' } },
      { x: 80, y: 50, label: { en: 'Assign to Container', fr: 'Affecter à un conteneur' },
        text: { en: 'Bulk-assigns all the group\'s cars to a new container in one click.', fr: 'Affecte toutes les voitures du groupe à un nouveau conteneur en un clic.' } },
    ],
    steps: [
      { en: 'Open Loading Groups. Pick the Dealers or Families tab.', fr: 'Ouvrez Groupes de chargement. Choisissez l\'onglet Concessionnaires ou Familles.' },
      { en: 'Click "View cars" to see what\'s inside, then "Assign to Container".', fr: 'Cliquez « Voir voitures » pour les détails, puis « Affecter à un conteneur ».' },
    ],
    permissions: { en: 'Admin, logistics manager, operations.', fr: 'Admin, responsable logistique, opérations.' },
  },
  {
    id: 'dedouanement',
    icon: 'Anchor',
    title: { en: 'Dédouanement (Ports)', fr: 'Dédouanement (Ports)' },
    intro: {
      en: 'List of Algerian ports (Alger, Oran, Béjaïa, Skikda...). Each car can be assigned to a port for customs clearance.',
      fr: 'Liste des ports algériens (Alger, Oran, Béjaïa, Skikda...). Chaque voiture peut être assignée à un port pour le dédouanement.',
    },
    screenshot: 'ports.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ Add Port', fr: '+ Ajouter Port' },
        text: { en: 'Name (e.g. Port d\'Alger), city, optional notes.', fr: 'Nom (ex. Port d\'Alger), ville, notes optionnelles.' } },
      { x: 65, y: 50, label: { en: 'View Containers', fr: 'Voir conteneurs' },
        text: { en: 'Shows every container currently assigned to that port grouped by container #.', fr: 'Affiche tous les conteneurs assignés à ce port, groupés par n°.' } },
    ],
    steps: [
      { en: 'Add the ports you use.', fr: 'Ajoutez les ports que vous utilisez.' },
      { en: 'In the Car edit drawer, pick the Port dropdown to assign each car.', fr: 'Dans la fiche voiture, choisissez le port pour assigner chaque voiture.' },
      { en: 'Click "View Containers" on any port to see which containers are clearing there.', fr: 'Cliquez « Voir conteneurs » sur un port pour voir lesquels y transitent.' },
    ],
    permissions: { en: 'Admin, logistics manager, operations.', fr: 'Admin, responsable logistique, opérations.' },
  },
  {
    id: 'douane-pos',
    icon: 'Receipt',
    title: { en: 'Douane POs', fr: 'POs Douane' },
    intro: {
      en: 'Separate purchase orders for customs clearance fees. Per car, with structured fee types (Dédouanement, Transiteur, Dégroupeur, Transport, Assurance, Pénalité, Magazinage). PDF lists every car, every fee, paid/due status, sub-totals and a global Total Dû.',
      fr: 'Bons de commande séparés pour les frais de dédouanement. Par voiture, avec types structurés (Dédouanement, Transiteur, Dégroupeur, Transport, Assurance, Pénalité, Magazinage). Le PDF liste chaque voiture, chaque frais, statut payé/dû, sous-totaux et Total Dû global.',
    },
    screenshot: 'douane-pos.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ New Douane PO', fr: '+ Nouveau PO Douane' },
        text: { en: 'Title, port, status (Draft/Issued/Paid/Closed), and a car picker.', fr: 'Titre, port, statut (Brouillon/Émis/Payé/Clôturé), et sélecteur de voitures.' } },
      { x: 50, y: 50, label: { en: 'Per-car fee editor', fr: 'Éditeur de frais par voiture' },
        text: { en: '7 preset fee types. Transiteur shows two prices: client price and internal cost (hidden from client; admin sees margin).', fr: '7 types de frais préréglés. Transiteur affiche deux prix : prix client et coût interne (caché du client ; admin voit la marge).' } },
      { x: 85, y: 70, label: { en: 'Download PDF', fr: 'Télécharger PDF' },
        text: { en: 'Clean PDF for the client. Internal costs are NEVER printed.', fr: 'PDF propre pour le client. Les coûts internes ne sont JAMAIS imprimés.' } },
    ],
    steps: [
      { en: 'Click "+ New Douane PO". Give it a title and select cars.', fr: 'Cliquez « + Nouveau PO Douane ». Donnez un titre et sélectionnez les voitures.' },
      { en: 'Expand each car to add its fees. Tick "Paid" once received.', fr: 'Dépliez chaque voiture pour ajouter ses frais. Cochez « Payé » à réception.' },
      { en: 'For Transiteur only: enter both the client price AND the internal cost so the margin is tracked. Internal cost is HIDDEN from the client PDF.', fr: 'Pour Transiteur uniquement : saisissez le prix client ET le coût interne pour suivre la marge. Le coût interne est CACHÉ du PDF client.' },
      { en: 'Click PDF to download. The footer shows Total Dédouanement + Total Dû (or "Tout est payé" if everything is settled).', fr: 'Cliquez PDF pour télécharger. Le pied affiche Total Dédouanement + Total Dû (ou « Tout est payé » si tout est réglé).' },
    ],
    permissions: { en: 'Admin / logistics manager / operations can create. Sales agents see only client prices, not internal cost or margin.', fr: 'Admin / responsable logistique / opérations peuvent créer. Les agents commerciaux ne voient que les prix client, pas le coût interne ni la marge.' },
  },
  {
    id: 'reports',
    icon: 'ChartBar',
    title: { en: 'Reports', fr: 'Rapports' },
    intro: {
      en: 'Aggregated KPIs across orders, payments, stock and shipments.',
      fr: 'Indicateurs agrégés sur commandes, paiements, stock et expéditions.',
    },
    screenshot: 'reports.png',
    callouts: [
      { x: 50, y: 30, label: { en: 'KPI grid', fr: 'Grille d\'indicateurs' },
        text: { en: 'Total orders, cars, revenue, collected, outstanding, stock.', fr: 'Total commandes, voitures, CA, encaissé, en attente, stock.' } },
    ],
    steps: [
      { en: 'Open Reports to see all the numbers at a glance.', fr: 'Ouvrez Rapports pour voir tous les chiffres en un coup d\'œil.' },
    ],
    permissions: { en: 'Admin and accountant.', fr: 'Admin et comptable.' },
  },
  {
    id: 'employees',
    icon: 'UserGear',
    title: { en: 'Employees & Permissions', fr: 'Employés & Permissions' },
    intro: {
      en: 'Create staff accounts with granular permissions and per-employee Telegram chat ID.',
      fr: 'Créez des comptes employés avec permissions granulaires et chat ID Telegram par employé.',
    },
    screenshot: 'employees.png',
    callouts: [
      { x: 92, y: 12, label: { en: '+ Add Employee', fr: '+ Ajouter Employé' },
        text: { en: 'Set email, password, role and permissions (e.g. "See Own Orders Only", "See Purchase Price").', fr: 'Définissez email, mot de passe, rôle et permissions (ex. « Voir uniquement ses propres commandes », « Voir Prix d\'Achat »).' } },
      { x: 65, y: 50, label: { en: 'Edit permissions', fr: 'Modifier les permissions' },
        text: { en: 'Permission toggles control what each employee sees and can do across the app.', fr: 'Les permissions contrôlent ce que chaque employé voit et peut faire.' } },
    ],
    steps: [
      { en: 'Click "+ Add Employee". Pick role (admin / sales_agent / logistics_manager / operations / accountant).', fr: 'Cliquez « + Ajouter Employé ». Choisissez le rôle.' },
      { en: 'Tick relevant permissions. Sales agents have "See Purchase Price" OFF by default.', fr: 'Cochez les permissions nécessaires. Les agents commerciaux ont « Voir Prix d\'Achat » désactivé par défaut.' },
      { en: 'Add their Telegram Chat ID so they receive direct DM notifications.', fr: 'Ajoutez leur Telegram Chat ID pour recevoir des notifications DM.' },
    ],
    permissions: { en: 'Admin only.', fr: 'Admin uniquement.' },
  },
  {
    id: 'telegram',
    icon: 'TelegramLogo',
    title: { en: 'Telegram Notifications', fr: 'Notifications Telegram' },
    intro: {
      en: 'Every new order fires a rich Telegram message to admins + the assigned agent + the order creator.',
      fr: 'Chaque nouvelle commande déclenche un message Telegram détaillé aux admins + agent assigné + créateur.',
    },
    screenshot: 'telegram.png',
    callouts: [
      { x: 50, y: 50, label: { en: 'Bot configuration', fr: 'Configuration du bot' },
        text: { en: 'Bot token in backend .env. Per-employee Telegram chat ID set on their Employee record.', fr: 'Jeton bot dans backend .env. Chat ID Telegram par employé sur leur fiche.' } },
    ],
    steps: [
      { en: 'Each employee opens @userinfobot in Telegram, sends /start, copies their chat ID.', fr: 'Chaque employé ouvre @userinfobot sur Telegram, envoie /start, copie son chat ID.' },
      { en: 'Admin pastes the ID into the employee\'s record (Telegram field).', fr: 'L\'admin colle l\'ID dans la fiche employé (champ Telegram).' },
      { en: 'New order → personal DM to that employee with full details (brand, model, color, VIN, client, date).', fr: 'Nouvelle commande → DM personnel à cet employé avec tous les détails.' },
    ],
    permissions: { en: 'Admin sets the bot. Any role can receive DMs if their chat ID is filled.', fr: 'L\'admin configure le bot. Tout rôle reçoit les DMs si son chat ID est rempli.' },
  },
  {
    id: 'data-export',
    icon: 'PlugsConnected',
    title: { en: 'Data Export (Zapier / Sheets)', fr: 'Export Données (Zapier / Sheets)' },
    intro: {
      en: 'Every order change sends a flat-row webhook to Zapier — pipe it to Google Sheets, Excel, your CRM, anywhere.',
      fr: 'Chaque modification de commande envoie un webhook ligne-plate à Zapier — connectez-le à Google Sheets, Excel, votre CRM, n\'importe où.',
    },
    screenshot: 'data-export.png',
    callouts: [
      { x: 50, y: 50, label: { en: 'Webhook URL', fr: 'URL Webhook' },
        text: { en: 'Paste your Zapier "Catch Hook" URL — every order create/update fires it.', fr: 'Collez votre URL « Catch Hook » Zapier — chaque création/modification déclenche.' } },
    ],
    steps: [
      { en: 'In Zapier, create a Zap with "Webhooks by Zapier" → "Catch Hook". Copy the URL.', fr: 'Dans Zapier, créez un Zap avec « Webhooks by Zapier » → « Catch Hook ». Copiez l\'URL.' },
      { en: 'Open Data Export in the app and paste it.', fr: 'Ouvrez Export Données et collez-la.' },
      { en: 'Wire the next Zap step to Google Sheets, Airtable, or any tool.', fr: 'Branchez l\'étape suivante du Zap vers Google Sheets, Airtable, ou tout autre outil.' },
    ],
    permissions: { en: 'Admin only.', fr: 'Admin uniquement.' },
  },
  {
    id: 'pwa',
    icon: 'CarProfile',
    title: { en: 'Install on Phone (PWA)', fr: 'Installer sur Téléphone (PWA)' },
    intro: {
      en: 'The app installs like a native app on iOS and Android with an icon on your home screen and offline browsing of cached pages.',
      fr: 'L\'app s\'installe comme une app native sur iOS et Android avec une icône sur l\'écran d\'accueil et navigation hors ligne des pages mises en cache.',
    },
    screenshot: 'pwa.png',
    callouts: [
      { x: 50, y: 50, label: { en: 'Add to Home Screen', fr: 'Ajouter à l\'écran d\'accueil' },
        text: { en: 'iOS Safari: Share → Add to Home Screen. Android Chrome: tap "Install".', fr: 'iOS Safari : Partager → Sur l\'écran d\'accueil. Android Chrome : « Installer ».' } },
    ],
    steps: [
      { en: 'Open https://carexport360.net in Safari (iOS) or Chrome (Android).', fr: 'Ouvrez https://carexport360.net dans Safari (iOS) ou Chrome (Android).' },
      { en: 'iOS: tap the Share icon → "Add to Home Screen". Android: tap the menu → "Install app".', fr: 'iOS : icône Partager → « Sur l\'écran d\'accueil ». Android : menu → « Installer l\'application ».' },
      { en: 'Open it from your home screen — runs fullscreen, no browser bars.', fr: 'Ouvrez depuis l\'écran d\'accueil — plein écran, sans barres de navigateur.' },
    ],
    permissions: { en: 'Available to all roles. Works only over HTTPS (production).', fr: 'Disponible pour tous les rôles. Fonctionne uniquement en HTTPS (production).' },
  },
];

export const UI_STRINGS = {
  pageTitle: { en: 'Help Center', fr: 'Centre d\'aide' },
  searchPlaceholder: { en: 'Search sections...', fr: 'Rechercher des sections...' },
  steps: { en: 'Steps', fr: 'Étapes' },
  permissions: { en: 'Permissions', fr: 'Permissions' },
  downloadPdf: { en: 'Download PDF', fr: 'Télécharger PDF' },
  downloadFullPdf: { en: 'Download full manual', fr: 'Télécharger manuel complet' },
  callout: { en: 'Numbered callouts', fr: 'Repères numérotés' },
  intro: { en: 'About this section', fr: 'À propos' },
  langSwitch: { en: 'Language', fr: 'Langue' },
  printable: { en: 'Printable', fr: 'Imprimable' },
};
